import React, { useState, useEffect } from 'react';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useIsMobile } from '@/hooks/use-mobile';
import { SportType } from '@/types';
import { Info, RotateCcw } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';

interface PointsFilterProps {
  value: number;
  setValue: (value: number) => void;
  onValueCommit?: (value: number) => void;
  sportType: SportType;
  setSportType: (type: SportType) => void;
  minValue: number;
  maxValue: number;
  defaultValues?: Record<SportType, number>;
  children?: React.ReactNode;
}

const PointsFilter: React.FC<PointsFilterProps> = ({ 
  value, 
  setValue,
  onValueCommit,
  sportType,
  setSportType,
  minValue = 0,
  maxValue = 50,
  defaultValues = {
    'nba': 10,    // Point Difference - Shows competitive games
    'soccer': 1,   // Total Goals - Only shows games with at least 1 goal
    'nfl': 7,     // Point Difference - Shows competitive games (1 score difference)
    'mlb': 3      // Total Runs - Shows competitive games
  },
  children
}) => {
  const isMobile = useIsMobile();
  const [inputValue, setInputValue] = useState<string>(value.toString());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sliderPosition, setSliderPosition] = useState<number>(0);

  useEffect(() => {
    // Update the input value when the value prop changes
    setInputValue(value.toString());
    // Calculate slider position percentage
    const percent = ((value - minValue) / (maxValue - minValue)) * 100;
    setSliderPosition(percent);
  }, [value, minValue, maxValue]);

  const handleSliderChange = (newValue: number[]) => {
    const val = newValue[0];
    setValue(val);
    setInputValue(val.toString());
  };
  
  const handleSliderDragEnd = (newValue: number[]) => {
    const committedValue = newValue[0];
    onValueCommit?.(committedValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    
    // Allow typing "All" (case insensitive) for non-soccer sports
    if (val.toLowerCase() === "all" && sportType !== 'soccer') {
      setValue(maxValue);
      setInputValue(val);
      return;
    }
    
    // Always allow empty field during editing for all sports
    if (val === '') {
      setInputValue('');
      // Don't update the actual value immediately when clearing the field
      // This allows for temporary empty state during editing
      return;
    }
    
    // Only allow digits
    if (/^\d+$/.test(val)) {
      // Check if within length limits to prevent huge numbers
      if (val.length <= maxValue.toString().length) {
        setInputValue(val);
        
        // Only update actual value if it's a valid number within range
        const numValue = parseInt(val, 10);
        
        // For all sports, validate against min/max
        if (numValue >= minValue && numValue <= maxValue) {
          setValue(numValue);
        }
      }
    }
  };

  const handleInputBlur = () => {
    let committedValue = value;

    // If the input is "All" (case insensitive) for sports that aren't total-score based, keep it as "All"
    if (inputValue.toLowerCase() === "all" && sportType !== 'soccer' && sportType !== 'nhl') {
      setValue(maxValue);
      setInputValue("All");
      committedValue = maxValue;
      onValueCommit?.(committedValue);
      return;
    }
    
    // If empty, revert to current value
    if (inputValue.trim() === "") {
      setInputValue(value.toString());
      onValueCommit?.(committedValue);
      return;
    }
    
    const numValue = parseInt(inputValue, 10);
    if (isNaN(numValue)) {
      // If not a valid number, revert to current value
      setInputValue(value.toString());
    } else if (numValue < minValue) {
      // If below minimum, set to minimum
      setValue(minValue);
      setInputValue(minValue.toString());
      committedValue = minValue;
    } else if (numValue > maxValue) {
      // If above maximum, set to maximum
      setValue(maxValue);
      // For total-score sports, always show the number; for other sports, show "All"
      setInputValue(['soccer', 'nhl'].includes(sportType) ? maxValue.toString() : "All");
      committedValue = maxValue;
    } else {
      // Valid number within range - normalize display
      setValue(numValue);
      setInputValue(numValue.toString());
      committedValue = numValue;
    }

    onValueCommit?.(committedValue);
  };

  const handleReset = () => {
    const defaultValue = defaultValues[sportType];
    setValue(defaultValue);
    setInputValue(defaultValue.toString());
    onValueCommit?.(defaultValue);
  };

  const getTitleText = () => {
    const isMaxValue = value === maxValue;
    const isMinValue = value === minValue;
    
    switch (sportType) {
      case 'nba':
        return isMaxValue ? 'Show all NBA games' : `Points Difference ≤ ${value} points`;
      case 'soccer':
        return isMinValue ? 'Show all Soccer games' : `Total Score ≥ ${value} goals`;
      case 'nfl':
        return isMaxValue ? 'Show all NFL games' : `Points Difference ≤ ${value} points`;
      case 'mlb':
        return isMaxValue ? 'Show all MLB games' : `Run Difference ≤ ${value} runs`;
      case 'nhl':
        return isMinValue ? 'Show all NHL games' : `Total Goals ≥ ${value} goals`;
      default:
        return 'Filter Games';
    }
  };

  const getInfoDialogContent = () => {
    const isMaxValue = value === maxValue;
    const isMinValue = value === minValue;
    
    switch (sportType) {
      case 'nba':
        return (
          <>
            <p>All matches must meet the filter criteria to be displayed.</p>
            {isMaxValue ? (
              <p className="mt-2">This setting shows all NBA games regardless of the point difference.</p>
            ) : (
              <p className="mt-2">This filter shows NBA games where the point difference between teams is equal to or less than {value}.</p>
            )}
            <p className="mt-2">Lower values show more competitive games while higher values show more games overall.</p>
            <p className="mt-2">The minimum point difference for NBA games is 1 since games can't end in a tie.</p>
          </>
        );
      case 'soccer':
        return (
          <>
            <p>All matches must meet the filter criteria to be displayed.</p>
            {isMinValue ? (
              <p className="mt-2">This setting shows all Soccer games regardless of the total score.</p>
            ) : (
              <p className="mt-2">This filter shows soccer games where the total number of goals scored is equal to or more than {value}.</p>
            )}
            <p className="mt-2">Higher values show higher-scoring games while lower values show more games overall.</p>
            <p className="mt-2">Soccer games can end with a total score of 0 (0-0 ties).</p>
          </>
        );
      case 'nfl':
        return (
          <>
            <p>All matches must meet the filter criteria to be displayed.</p>
            {isMaxValue ? (
              <p className="mt-2">This setting shows all NFL games regardless of the point difference.</p>
            ) : (
              <p className="mt-2">This filter shows NFL games where the point difference between teams is equal to or less than {value}.</p>
            )}
            <p className="mt-2">Lower values show more competitive games while higher values show more games overall.</p>
            <p className="mt-2">The minimum point difference for NFL games is 1.</p>
          </>
        );
      case 'mlb':
        return (
          <>
            <p>All matches must meet the filter criteria to be displayed.</p>
            {isMaxValue ? (
              <p className="mt-2">This setting shows all MLB games regardless of the run difference.</p>
            ) : (
              <p className="mt-2">This filter shows MLB games where the run difference between teams is equal to or less than {value}.</p>
            )}
            <p className="mt-2">Lower values show more competitive games while higher values show more games overall.</p>
            <p className="mt-2">MLB games can end with a run difference of 0 in extra innings.</p>
          </>
        );
      case 'nhl':
        return (
          <>
            <p>All matches must meet the filter criteria to be displayed.</p>
            {isMinValue ? (
              <p className="mt-2">This setting shows all NHL games regardless of the total score.</p>
            ) : (
              <p className="mt-2">This filter shows NHL games where the total number of goals scored is equal to or more than {value}.</p>
            )}
            <p className="mt-2">Higher values show higher-scoring games while lower values show more games overall.</p>
            <p className="mt-2">NHL games can end in low-scoring affairs, including 0-0 ties that go to overtime or shootout.</p>
          </>
        );
      default:
        return <p>Adjust the filter to see games that match your criteria.</p>;
    }
  };

  const getSportUnit = () => {
    switch (sportType) {
      case 'nba':
      case 'nfl':
        return value === 1 ? 'point' : 'points';
      case 'soccer':
      case 'nhl':
        return value === 1 ? 'goal' : 'goals';
      case 'mlb':
        return value === 1 ? 'run' : 'runs';
      default:
        return value === 1 ? 'point' : 'points';
    }
  };
  
  // Determine if the value is at maximum to show "All" instead of the numeric value
  const getDisplayValue = () => {
    // During active editing, always show exactly what the user is typing
    if (document.activeElement === document.querySelector('[data-custom-name="value-input"]')) {
      return inputValue;
    }
    
    // For total-score sports, we don't use "All" - just show the numeric value
    if (sportType === 'soccer' || sportType === 'nhl') {
      return value.toString();
    }
    
    // Otherwise use the normal display rules
    return value === maxValue ? "All" : value.toString();
  };
  
  return (
    <Card className="mb-4 bg-gray-800 border-gray-700 rounded-xl shadow-lg transition-all duration-300 hover:shadow-indigo-900/20" data-custom-name="filter-card">
      <CardContent className="pt-3 pb-2">
        {/* Additional filters section (e.g., CompetitionFilter) */}
        {children && (
          <div className="mb-2" data-custom-name="additional-filters">
            {children}
            <Separator className="my-3 bg-gray-700" />
          </div>
        )}
        
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-gray-200">{getTitleText()}</h3>
              
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <button 
                    className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-gray-200 cursor-pointer transition-colors duration-200" 
                    aria-label="Filter information"
                    data-custom-name="info-button"
                  >
                    <Info className="h-4 w-4" />
                    <span className="text-xs font-medium">Filter Info</span>
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-gray-800 text-gray-200 border-gray-700 rounded-xl animate-scale-in">
                  <DialogHeader>
                    <DialogTitle>Filter Information</DialogTitle>
                  </DialogHeader>
                  <div className="py-3">
                    {getInfoDialogContent()}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            <div className="flex items-center gap-2" data-custom-name="input-controls">
              <Input
                type="text"
                min={minValue}
                max={maxValue}
                value={getDisplayValue()}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                className="w-16 text-center bg-gray-700 border-gray-600 text-gray-200 rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 h-8"
                data-custom-name="value-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                className="h-8 bg-gray-700 border-gray-600 text-gray-200 hover:bg-gray-600 hover:text-white transition-all duration-200"
                aria-label="Reset to default value"
                data-custom-name="reset-button"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1" />
                Default
              </Button>
            </div>
          </div>
          
          <div className="py-2 sm:py-6 relative mt-8 sm:mt-2" data-custom-name="slider-container">
            <div className="relative">
              {/* Value indicator bubble - Properly adjusted spacing for mobile and desktop */}
              <AnimatePresence>
                <motion.div 
                  className="absolute -top-8 sm:-top-10 transform -translate-x-1/2 z-10"
                  style={{ left: `${sliderPosition}%` }}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                  data-custom-name="value-bubble"
                >
                  <div className="bg-indigo-600 text-white px-4 py-1 rounded-lg font-bold min-w-[50px] text-center shadow-xl border border-indigo-400">
                    {getDisplayValue()}
                  </div>
                </motion.div>
              </AnimatePresence>
              
              {/* Slider with properly adjusted gap */}
              <div className="mt-3 sm:mt-0" data-custom-name="slider-wrapper">
                <Slider
                  value={[value]}
                  max={maxValue}
                  min={minValue}
                  step={1}
                  onValueChange={handleSliderChange}
                  onValueCommit={handleSliderDragEnd}
                  className="w-full cursor-pointer"
                  data-custom-name="slider"
                />
              </div>
              
              {/* Limit indicators */}
              <div className="absolute -top-2 left-0 w-full" data-custom-name="limit-indicators">
                <div className="w-0.5 h-3 bg-gray-600 absolute left-0"></div>
                <div className="w-0.5 h-3 bg-gray-600 absolute left-1/2 -translate-x-1/2"></div>
                <div className="w-0.5 h-3 bg-gray-600 absolute right-0"></div>
              </div>
            </div>
            
            <div className="flex justify-between mt-3 text-xs text-gray-400" data-custom-name="range-labels">
              <span className="transition-all duration-200 hover:text-gray-200">{minValue}</span>
              <span className="transition-all duration-200 hover:text-gray-200">{Math.floor((maxValue + minValue) / 2)}</span>
              <span className="transition-all duration-200 hover:text-gray-200">{['soccer', 'nhl'].includes(sportType) ? maxValue : 'All'}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PointsFilter;
